import React, { Component, ReactNode } from 'react';
import { AuthorizationError, isAuthorizationError } from '../../types/errors';
import { AccountSwitcherModal } from './AccountSwitcherModal';
import { authLogger } from '../../utils/logger';

interface Props {
  children: ReactNode;
}

interface State {
  authError: AuthorizationError | null;
  hasError: boolean;
}

/**
 * Error boundary specifically for authorization errors (401/403/404)
 * Catches errors from API calls and displays appropriate UI
 */
export class AuthorizationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      authError: null,
      hasError: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is an authorization error
    if (isAuthorizationError(error)) {
      authLogger.error('Authorization error caught by boundary:', {
        type: error.type,
        status: error.status,
        message: error.userMessage
      });

      return {
        hasError: true,
        authError: error
      };
    }

    // Let other errors propagate to parent error boundaries
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isAuthorizationError(error)) {
      authLogger.error('Authorization error details:', {
        error,
        errorInfo,
        componentStack: errorInfo.componentStack
      });
    }
  }

  handleDismiss = (): void => {
    this.setState({
      hasError: false,
      authError: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.authError) {
      return (
        <AccountSwitcherModal
          error={this.state.authError}
          onDismiss={this.handleDismiss}
        />
      );
    }

    return this.props.children;
  }
}
